import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Shield,
  ClipboardCheck,
  Search,
  AlertTriangle,
  FileCheck,
  CheckCircle2,
  List,
  BookOpen,
  Thermometer,
  Bug,
  Trash2,
  Users,
  Droplets,
  Calendar,
  Clock,
  FileText,
  BadgeCheck,
} from 'lucide-react';

/* --- FAQs ------------------------------------------------- */

const faqs = [
  {
    q: '¿Con que frecuencia pasan las inspecciones de sanidad en restaurantes?',
    a: 'Depende de la clasificacion de riesgo del establecimiento. Los restaurantes de alto riesgo (gran volumen, cocina compleja) pueden recibir entre 1 y 3 inspecciones al ano. Los de riesgo medio, una cada 1-2 anos. Los de bajo riesgo, cada 2-3 anos. Sin embargo, cualquier denuncia de un cliente puede provocar una inspeccion inmediata fuera de calendario.',
  },
  {
    q: '¿Pueden cerrar mi restaurante en una inspeccion?',
    a: 'Si. Si el inspector detecta un riesgo grave e inminente para la salud publica (por ejemplo, plagas activas, alimentos en descomposicion, falta total de refrigeracion), puede ordenar el cierre cautelar inmediato del establecimiento. El cierre se mantiene hasta que se corrijan las deficiencias y se supere una reinspeccion.',
  },
  {
    q: '¿Es obligatorio tener un plan APPCC en mi restaurante?',
    a: 'Si. El Reglamento (CE) 852/2004 obliga a todos los establecimientos alimentarios a implementar un sistema de autocontrol basado en los principios APPCC (Analisis de Peligros y Puntos de Control Critico). No tener un plan APPCC es una infraccion grave que puede acarrear multas de 2.001 a 30.000 euros.',
  },
  {
    q: '¿Que pasa si me ponen una sancion leve?',
    a: 'Las sanciones leves oscilan entre 200 y 2.000 euros segun la Ley 17/2011. Normalmente el inspector da un plazo de correccion (entre 15 y 30 dias) para subsanar las deficiencias. Si las corriges dentro del plazo, la sancion puede reducirse o no aplicarse. Si reincides, la siguiente infraccion se clasifica un grado superior.',
  },
  {
    q: '¿Necesitan los manipuladores de alimentos un carnet o certificado?',
    a: 'Si. Todos los trabajadores que manipulan alimentos deben tener formacion acreditada en higiene alimentaria. Aunque ya no se exige un "carnet de manipulador" como tal, el Real Decreto 109/2010 obliga al empresario a garantizar que su personal tenga la formacion adecuada y a conservar los certificados como prueba.',
  },
  {
    q: '¿Pueden venir a inspeccionar sin avisar?',
    a: 'Si. Las inspecciones sanitarias se realizan sin previo aviso. De hecho, la normativa europea (Reglamento CE 882/2004) establece que los controles oficiales deben realizarse de forma periodica y sin notificacion previa al operador, salvo en casos excepcionales donde sea necesaria la colaboracion del establecimiento.',
  },
  {
    q: '¿Que temperatura deben tener las camaras frigorificas?',
    a: 'La camara de refrigeracion debe estar entre 0 y 4 grados centigrados. El congelador debe mantenerse a -18 grados centigrados o menos. Los registros de temperatura deben tomarse al menos dos veces al dia (apertura y cierre) y conservarse durante al menos un ano. Cualquier desviacion debe registrarse con la accion correctiva aplicada.',
  },
  {
    q: '¿Cuanto tiempo debo conservar los registros de autocontrol?',
    a: 'La normativa general exige conservar los registros de autocontrol (temperaturas, limpieza, recepcion de mercancias, trazabilidad) durante un minimo de 2 anos, aunque se recomienda mantenerlos 5 anos. Los certificados de formacion de manipuladores y los contratos de control de plagas deben estar siempre disponibles para inspeccion.',
  },
];

/* --- Table of Contents ------------------------------------ */

const toc = [
  { id: 'que-es', label: 'Que es una inspeccion sanitaria y quien la realiza' },
  { id: 'cuando-vienen', label: 'Cuando pueden venir: frecuencia y aviso previo' },
  { id: '10-puntos', label: 'Los 10 puntos que revisa un inspector de sanidad' },
  { id: 'infracciones', label: 'Infracciones y sanciones: tabla completa' },
  { id: 'errores-comunes', label: 'Las 5 infracciones mas comunes' },
  { id: 'checklist', label: 'Checklist: 15 puntos para superar la inspeccion' },
  { id: 'si-suspendes', label: 'Que pasa si suspendes la inspeccion' },
  { id: 'documentacion', label: 'Documentacion que debes tener siempre a mano' },
  { id: 'rutinas', label: 'Como mantener tu restaurante siempre listo' },
  { id: 'herramientas', label: 'Herramientas digitales para el control sanitario' },
  { id: 'faqs', label: 'Preguntas frecuentes' },
];

/* --- Inspection points ------------------------------------ */

const inspectionPoints = [
  { num: 1, title: 'Plan APPCC y registros de autocontrol', icon: FileCheck, desc: 'El inspector verificara que tienes un plan APPCC documentado, actualizado y que los registros se cumplen a diario: temperaturas, limpieza, recepcion de mercancias y acciones correctivas.' },
  { num: 2, title: 'Control de temperaturas', icon: Thermometer, desc: 'Camaras frigorificas (0-4 grados), congeladores (-18 grados o menos), temperatura de servicio en caliente (>65 grados). Los termometros deben estar calibrados y los registros al dia.' },
  { num: 3, title: 'Limpieza e higiene general', icon: Droplets, desc: 'Superficies de trabajo, suelos, paredes, campanas extractoras, banos de personal y clientes. Se revisan los planes de limpieza y desinfeccion con productos homologados.' },
  { num: 4, title: 'Formacion de manipuladores', icon: Users, desc: 'Certificados de formacion en higiene alimentaria de todo el personal. El empresario debe acreditar que cada trabajador tiene la formacion adecuada para su puesto.' },
  { num: 5, title: 'Gestion de alergenos', icon: Shield, desc: 'Informacion de alergenos disponible para el cliente segun el Reglamento UE 1169/2011. Fichas tecnicas de ingredientes y procedimiento para evitar contaminacion cruzada.' },
  { num: 6, title: 'Control de plagas (DDD)', icon: Bug, desc: 'Contrato vigente con empresa de Desinsectacion, Desratizacion y Desinfeccion (DDD). Registros de tratamientos, planos de cebos, ausencia de indicios de plagas.' },
  { num: 7, title: 'Estado de instalaciones y equipos', icon: ClipboardCheck, desc: 'Buen estado de conservacion de cocina, almacen, camaras, campanas. Ventilacion adecuada, iluminacion suficiente, separacion de zonas (sucio/limpio).' },
  { num: 8, title: 'Gestion de residuos', icon: Trash2, desc: 'Contenedores con tapa y pedal, vaciado frecuente, separacion de residuos segun normativa municipal. Zona de basuras separada de la zona de manipulacion de alimentos.' },
  { num: 9, title: 'Trazabilidad de productos', icon: Search, desc: 'Sistema que permita identificar el origen de cada producto: albaranes, facturas, etiquetado de productos recibidos, lotes. Capacidad de rastrear un producto desde el proveedor hasta el plato.' },
  { num: 10, title: 'Calidad del agua', icon: Droplets, desc: 'Si el establecimiento usa agua de red municipal, generalmente no requiere analisis. Si usa pozo propio o deposito, debe tener analisis periodicos y tratamiento de potabilizacion documentado.' },
];

/* --- Schema.org JSON-LD ----------------------------------- */

const faqJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.q,
    acceptedAnswer: { '@type': 'Answer', text: faq.a },
  })),
};

const breadcrumbJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    { '@type': 'ListItem', position: 1, name: 'PLEKO', item: 'https://pleko.app/' },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Inspeccion de Sanidad en Restaurantes',
      item: 'https://pleko.app/inspeccion-sanidad-restaurante',
    },
  ],
};

const articleJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Article',
  headline: 'Inspeccion de Sanidad en Restaurantes: Que Revisan, Multas y Como Prepararte',
  description:
    'Guia completa sobre inspecciones sanitarias en restaurantes en Espana. Que revisan los inspectores, tabla de multas, las 5 infracciones mas comunes y checklist de 15 puntos para superar la inspeccion.',
  author: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  publisher: { '@type': 'Organization', name: 'PLEKO', url: 'https://pleko.app' },
  datePublished: '2026-06-18',
  dateModified: '2026-06-18',
  mainEntityOfPage: 'https://pleko.app/inspeccion-sanidad-restaurante',
};

/* --- Checklist items -------------------------------------- */

const checklistItems = [
  'Plan APPCC documentado, actualizado y firmado por el responsable',
  'Registros de temperatura de camaras y congeladores al dia (2 tomas/dia)',
  'Certificados de formacion de todos los manipuladores de alimentos',
  'Contrato DDD vigente con empresa autorizada y registros de tratamientos',
  'Fichas de alergenos de todos los platos de la carta',
  'Plan de limpieza y desinfeccion con registro de ejecucion',
  'Termometros calibrados (al menos uno por camara y uno de sonda)',
  'Albaranes y facturas de proveedores archivados (trazabilidad)',
  'Contenedores de basura con tapa, pedal y bolsa, vaciado frecuente',
  'Zona de recepcion de mercancias limpia y procedimiento documentado',
  'Separacion fisica entre zona sucia y zona limpia en cocina',
  'Productos quimicos almacenados separados de los alimentos',
  'Etiquetado correcto de productos abiertos (fecha apertura, caducidad secundaria)',
  'Agua potable certificada (si es pozo o deposito, analisis vigente)',
  'Vestuario de personal con taquillas, lavamanos y jabon bactericida',
];

/* --- Component -------------------------------------------- */

export default function InspeccionSanidadRestaurantePage() {
  return (
    <>
      <Helmet>
        <title>Inspeccion de Sanidad en Restaurantes: Que Revisan, Multas y Como Prepararte | PLEKO</title>
        <meta
          name="description"
          content="Guia completa sobre inspecciones sanitarias en restaurantes. Los 10 puntos que revisan, tabla de multas (200-600.000 euros), las 5 infracciones mas comunes y checklist de 15 puntos para superar la inspeccion."
        />
        <meta
          name="keywords"
          content="inspeccion sanidad restaurante, inspeccion sanitaria restaurante, multas sanidad restaurante, que revisan en una inspeccion de sanidad, inspector sanitario restaurante, inspeccion sanitaria hosteleria, APPCC restaurante, sanciones sanidad restaurante"
        />
        <link rel="canonical" href="https://pleko.app/inspeccion-sanidad-restaurante" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://pleko.app/inspeccion-sanidad-restaurante" />
        <meta
          property="og:title"
          content="Inspeccion de Sanidad en Restaurantes: Que Revisan, Multas y Como Prepararte"
        />
        <meta
          property="og:description"
          content="Los 10 puntos que revisan los inspectores, tabla de multas y checklist de 15 puntos para superar la inspeccion sanitaria."
        />
        <meta property="og:image" content="https://pleko.app/og-image.png" />
        <meta property="og:locale" content="es_ES" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Inspeccion de Sanidad en Restaurantes: Que Revisan y Multas | PLEKO"
        />
        <meta
          name="twitter:description"
          content="Guia completa: 10 puntos que revisan, multas de 200 a 600.000 euros y checklist para superar la inspeccion."
        />
        <meta name="twitter:image" content="https://pleko.app/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbJsonLd)}</script>
        <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      </Helmet>

      <div className="bg-white">
        {/* ====== HERO ====== */}
        <section className="bg-gradient-to-b from-red-50 to-white py-16 lg:py-24">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium mb-6">
                <Shield className="w-4 h-4" />
                Guia para superar inspecciones de sanidad — 2026
              </span>
              <h1 className="text-4xl sm:text-5xl font-extrabold text-slate-900 mb-6 tracking-tight">
                Inspeccion de Sanidad en Restaurantes:{' '}
                <span className="text-red-600">Que Revisan, Multas y Como Prepararte</span>
              </h1>
              <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
                Multas de hasta <strong>600.000 euros</strong> y cierre cautelar inmediato. Conoce los 10 puntos que revisa un inspector sanitario, la tabla completa de sanciones y el checklist de 15 puntos para que tu restaurante este siempre preparado.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <a
                  href="#10-puntos"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-red-600 text-white font-semibold rounded-xl hover:bg-red-700 transition-colors shadow-lg shadow-red-200"
                >
                  Ver los 10 puntos de inspeccion
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link
                  to="/register"
                  className="inline-flex items-center gap-2 px-6 py-3 border-2 border-slate-200 text-slate-700 font-semibold rounded-xl hover:border-slate-300 transition-colors"
                >
                  <ClipboardCheck className="w-4 h-4" />
                  Probar PLEKO gratis
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* ====== CONTENT ====== */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Table of Contents */}
          <nav className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-12">
            <div className="flex items-center gap-2 mb-4">
              <List className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-bold text-slate-900">Contenido de esta guia</h2>
            </div>
            <ol className="grid sm:grid-cols-2 gap-x-8 gap-y-2">
              {toc.map((item, i) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} className="text-sm text-red-600 hover:text-red-800 hover:underline">
                    {i + 1}. {item.label}
                  </a>
                </li>
              ))}
            </ol>
          </nav>

          <div className="prose prose-lg max-w-none">
            {/* ====== SECTION 1: QUE ES ====== */}
            <h2 id="que-es" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Que es una inspeccion sanitaria y quien la realiza
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Una inspeccion sanitaria es un control oficial que realizan las <strong>autoridades de salud publica autonomicas</strong> para verificar que los establecimientos alimentarios cumplen la normativa vigente en materia de seguridad alimentaria e higiene. En Espana, la competencia recae en las Consejerias de Sanidad de cada comunidad autonoma.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Los inspectores son funcionarios con formacion en veterinaria, farmacia, ciencia y tecnologia de los alimentos u otras disciplinas sanitarias. Su funcion no es "pillar" al restaurador, sino <strong>proteger la salud publica</strong> y garantizar que los alimentos que se sirven son seguros para el consumidor.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La base legal principal es el <strong>Reglamento (CE) 852/2004</strong> sobre higiene de los productos alimenticios y la <strong>Ley 17/2011</strong> de seguridad alimentaria y nutricion. Estas normas aplican a todos los establecimientos de hosteleria: restaurantes, bares, cafeterias, catering, comedores colectivos y cualquier negocio que elabore o sirva alimentos.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-10">
              En la practica, cada comunidad autonoma tiene su propia agencia o servicio de salud publica que coordina las inspecciones: en Cataluna es la <strong>ASPCAT</strong> (Agencia de Salut Publica de Catalunya), en Madrid la <strong>Direccion General de Salud Publica</strong>, en Andalucia la <strong>Consejeria de Salud y Consumo</strong>, etc. Los protocolos son similares en toda Espana porque se basan en la misma legislacion europea, pero pueden existir diferencias en frecuencias y procedimientos administrativos.
            </p>

            {/* ====== SECTION 2: CUANDO VIENEN ====== */}
            <h2 id="cuando-vienen" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Cuando pueden venir: frecuencia y aviso previo
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Las inspecciones sanitarias se realizan <strong>sin previo aviso</strong>. El Reglamento (CE) 882/2004 establece que los controles oficiales deben efectuarse sin notificacion previa al operador de empresa alimentaria, salvo excepciones justificadas como auditorias programadas.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La frecuencia depende de la <strong>clasificacion de riesgo</strong> del establecimiento, que tiene en cuenta factores como el volumen de produccion, el tipo de elaboraciones, el publico al que se dirige (poblacion vulnerable como hospitales o colegios) y el historial de cumplimiento:
            </p>
            <div className="grid md:grid-cols-3 gap-4 mb-10">
              <div className="p-5 bg-red-50 border border-red-200 rounded-xl text-center">
                <p className="text-3xl font-bold text-red-600 mb-1">1-3</p>
                <p className="text-sm font-semibold text-slate-800">inspecciones/ano</p>
                <p className="text-xs text-slate-500 mt-1">Riesgo alto: hospitales, colegios, gran volumen</p>
              </div>
              <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl text-center">
                <p className="text-3xl font-bold text-amber-600 mb-1">1</p>
                <p className="text-sm font-semibold text-slate-800">inspeccion cada 1-2 anos</p>
                <p className="text-xs text-slate-500 mt-1">Riesgo medio: restaurantes con cocina completa</p>
              </div>
              <div className="p-5 bg-green-50 border border-green-200 rounded-xl text-center">
                <p className="text-3xl font-bold text-green-600 mb-1">1</p>
                <p className="text-sm font-semibold text-slate-800">inspeccion cada 2-3 anos</p>
                <p className="text-xs text-slate-500 mt-1">Riesgo bajo: bares, cafeterias con servicio basico</p>
              </div>
            </div>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ademas de las inspecciones programadas, existen las <strong>inspecciones reactivas</strong> que se desencadenan por eventos concretos: denuncias de clientes, brotes de toxiinfeccion alimentaria, alertas de la Red de Alerta Alimentaria (SCIRI) o campanas sectoriales impulsadas por la administracion. Estas inspecciones tienen prioridad y suelen ser mas exhaustivas que las rutinarias.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Importante</p>
                  <p className="text-amber-700 text-sm mt-1">
                    Cualquier denuncia de un cliente, intoxicacion alimentaria notificada o alerta sanitaria puede provocar una <strong>inspeccion inmediata fuera de calendario</strong>, independientemente de la clasificacion de riesgo del establecimiento. Una sola resena negativa mencionando una intoxicacion puede activar una inspeccion en cuestion de dias.
                  </p>
                </div>
              </div>
            </div>

            {/* ====== SECTION 3: 10 PUNTOS ====== */}
            <h2 id="10-puntos" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Los 10 puntos que revisa un inspector de sanidad
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Cuando un inspector sanitario entra en tu restaurante, revisa de forma sistematica estas 10 areas. No siempre revisa las 10 en una sola visita — dependiendo del tipo de inspeccion puede centrarse en 3 o 4 — pero debes estar preparado para todas. Conocerlas a fondo es el primer paso para superar cualquier control:
            </p>
            <div className="space-y-4 mb-10">
              {inspectionPoints.map(({ num, title, icon: Icon, desc }) => (
                <div key={num} className="flex gap-4 p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex-shrink-0 flex flex-col items-center gap-1">
                    <span className="w-10 h-10 rounded-full bg-red-600 text-white font-bold flex items-center justify-center text-lg">{num}</span>
                    <Icon className="w-4 h-4 text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
                    <p className="text-slate-600 text-sm">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ====== SECTION 4: TABLA INFRACCIONES ====== */}
            <h2 id="infracciones" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Infracciones y sanciones: tabla completa (Ley 17/2011)
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La <strong>Ley 17/2011 de seguridad alimentaria y nutricion</strong> clasifica las infracciones en tres niveles. Las sanciones pueden ser economicas, de cierre temporal o incluso de inhabilitacion profesional:
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="bg-slate-800 text-white">
                    <th className="p-3 text-left font-medium">Tipo</th>
                    <th className="p-3 text-center font-medium">Sancion economica</th>
                    <th className="p-3 text-left font-medium">Ejemplos</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Leve', '200 - 2.000 euros', 'Deficiencias menores de higiene, registros incompletos, carnet de manipulador caducado'],
                    ['Grave', '2.001 - 30.000 euros', 'No tener plan APPCC, temperaturas fuera de rango reiteradas, falta de trazabilidad, no declarar alergenos'],
                    ['Muy grave', '30.001 - 600.000 euros', 'Riesgo real para la salud, ocultacion de datos, reincidencia en infracciones graves, venta de alimentos no aptos'],
                  ].map(([tipo, sancion, ejemplos], i) => (
                    <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50'}>
                      <td className="p-3 border-b border-slate-100 font-semibold text-slate-800">
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-bold ${
                          tipo === 'Leve' ? 'bg-amber-100 text-amber-700' :
                          tipo === 'Grave' ? 'bg-red-100 text-red-700' :
                          'bg-red-200 text-red-900'
                        }`}>
                          {tipo}
                        </span>
                      </td>
                      <td className="p-3 border-b border-slate-100 text-center font-bold text-red-600">{sancion}</td>
                      <td className="p-3 border-b border-slate-100 text-slate-600">{ejemplos}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-xl p-5 mb-10">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800">Ademas de la multa economica</p>
                  <p className="text-red-700 text-sm mt-1">
                    Las infracciones graves y muy graves pueden conllevar el <strong>cierre temporal del establecimiento</strong> (de 1 a 5 anos en casos muy graves), la <strong>retirada de licencias</strong>, la publicacion del nombre del establecimiento sancionado y la <strong>inhabilitacion del responsable</strong> para ejercer en el sector alimentario.
                  </p>
                </div>
              </div>
            </div>

            {/* ====== SECTION 5: 5 INFRACCIONES MAS COMUNES ====== */}
            <h2 id="errores-comunes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Las 5 infracciones mas comunes en restaurantes
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Segun datos de las inspecciones realizadas en Espana, estos son los 5 incumplimientos que los inspectores detectan con mas frecuencia. La buena noticia es que todos son facilmente evitables con las herramientas y rutinas adecuadas. De hecho, la mayoria de restaurantes que "suspenden" una inspeccion lo hacen por falta de organizacion documental, no por problemas graves de higiene:
            </p>
            <div className="space-y-4 mb-10">
              {[
                {
                  num: 1,
                  title: 'Temperaturas incorrectas de conservacion',
                  desc: 'Camaras por encima de 4 grados, congeladores que no alcanzan los -18 grados, o alimentos calientes servidos por debajo de 65 grados. Es la infraccion numero uno porque es la mas facil de detectar con un termometro de sonda.',
                  fix: 'Instala termometros digitales con alarma, registra temperaturas 2 veces al dia y revisa las juntas de las puertas de camaras cada mes.',
                },
                {
                  num: 2,
                  title: 'Falta de registros de autocontrol',
                  desc: 'Tener un plan APPCC pero no rellenar los registros a diario. Muchos restaurantes tienen el documento pero las hojas de registro estan en blanco o incompletas. Un plan sin registros equivale a no tener plan.',
                  fix: 'Digitaliza los registros con una app o establece un responsable por turno que firme los registros antes de acabar la jornada.',
                },
                {
                  num: 3,
                  title: 'Limpieza deficiente en zonas criticas',
                  desc: 'Campanas extractoras con grasa acumulada, detras de equipos de cocina, juntas de camaras con moho, desagues obstruidos. Los inspectores revisan las zonas que no se ven a simple vista.',
                  fix: 'Implementa un plan de limpieza con frecuencias (diaria, semanal, mensual, trimestral) y asigna responsables para cada zona.',
                },
                {
                  num: 4,
                  title: 'Manipuladores sin formacion acreditada',
                  desc: 'Personal de cocina sin certificado de manipulador de alimentos o con formacion caducada. Desde 2010 la responsabilidad recae en el empresario, no en el trabajador.',
                  fix: 'Organiza una formacion anual para todo el equipo. Guarda los certificados en una carpeta accesible y actualiza los de nuevas incorporaciones.',
                },
                {
                  num: 5,
                  title: 'Alergenos no declarados o mal gestionados',
                  desc: 'No disponer de fichas de alergenos, no tener protocolos para evitar contaminacion cruzada o no informar al cliente. Desde 2014 es obligatorio en toda la UE (Reglamento 1169/2011).',
                  fix: 'Asigna alergenos a cada ingrediente en tu sistema de gestion. PLEKO lo hace automaticamente y los muestra en la carta digital.',
                },
              ].map(({ num, title, desc, fix }) => (
                <div key={num} className="p-5 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="flex items-start gap-3 mb-3">
                    <span className="w-8 h-8 rounded-full bg-amber-500 text-white font-bold flex items-center justify-center text-sm flex-shrink-0">{num}</span>
                    <h3 className="font-semibold text-slate-800 text-lg">{title}</h3>
                  </div>
                  <p className="text-slate-600 text-sm mb-3">{desc}</p>
                  <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-lg p-3">
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0 mt-0.5" />
                    <p className="text-green-800 text-sm"><strong>Solucion:</strong> {fix}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ====== SECTION 6: CHECKLIST 15 PUNTOS ====== */}
            <h2 id="checklist" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Checklist: 15 puntos para superar la inspeccion
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Hemos recopilado los 15 puntos criticos que los inspectores revisan con mayor frecuencia en un unico checklist practico. Imprimelo, cualgalo en la cocina y revisalo mensualmente con tu equipo. Si los 15 puntos estan en verde, tu restaurante esta preparado para cualquier inspeccion sin importar cuando llegue:
            </p>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-6 mb-10">
              <div className="space-y-3">
                {checklistItems.map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="w-6 h-6 rounded border-2 border-slate-300 flex-shrink-0 flex items-center justify-center text-xs font-bold text-slate-400 mt-0.5">{i + 1}</span>
                    <p className="text-slate-700 text-sm">{item}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* ====== SECTION 7: SI SUSPENDES ====== */}
            <h2 id="si-suspendes" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Que pasa si suspendes la inspeccion
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Si el inspector detecta incumplimientos, el procedimiento sigue estos pasos segun la gravedad. Es importante saber que el inspector esta obligado a levantar acta de todo lo que observa — tanto positivo como negativo — y el restaurante tiene derecho a hacer alegaciones por escrito en un plazo de 15 dias:
            </p>
            <div className="space-y-4 mb-10">
              {[
                {
                  title: 'Deficiencias leves: plazo de correccion',
                  desc: 'El inspector emite un acta con las deficiencias y da un plazo de 15 a 30 dias para subsanarlas. Se programa una reinspeccion para verificar que se han corregido. Si las corriges a tiempo, normalmente no hay sancion economica.',
                  icon: Clock,
                  color: 'amber',
                },
                {
                  title: 'Deficiencias graves: acta de infraccion + reinspeccion',
                  desc: 'Se inicia un procedimiento sancionador con un acta formal. Se da un plazo de correccion (15-30 dias) y se programa reinspeccion. Si en la reinspeccion las deficiencias persisten, la sancion se agrava. Posible cierre parcial de instalaciones afectadas.',
                  icon: AlertTriangle,
                  color: 'red',
                },
                {
                  title: 'Riesgo inminente: cierre cautelar inmediato',
                  desc: 'Si hay riesgo grave e inminente para la salud (plagas activas, alimentos en descomposicion, falta total de higiene), el inspector puede ordenar el cierre cautelar del establecimiento en el acto. No se puede reabrir hasta superar una reinspeccion completa.',
                  icon: Shield,
                  color: 'red',
                },
              ].map(({ title, desc, icon: Icon, color }) => (
                <div key={title} className={`p-5 rounded-xl border ${color === 'red' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'}`}>
                  <div className="flex items-start gap-3">
                    <Icon className={`w-5 h-5 flex-shrink-0 mt-0.5 ${color === 'red' ? 'text-red-600' : 'text-amber-600'}`} />
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-1">{title}</h3>
                      <p className="text-slate-600 text-sm">{desc}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* ====== SECTION 8: DOCUMENTACION ====== */}
            <h2 id="documentacion" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Documentacion que debes tener siempre a mano
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              El inspector puede pedir estos documentos en cualquier momento. La frase mas temida durante una inspeccion es <strong>"¿Me puede ensenar la documentacion?"</strong> seguida de minutos de busqueda nerviosa entre carpetas desorganizadas. Tenlos organizados en una carpeta fisica o digital accesible al instante — la organizacion documental transmite profesionalidad y genera confianza en el inspector:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-10">
              {[
                { title: 'Plan APPCC completo', desc: 'Con analisis de peligros, puntos criticos, limites, medidas correctivas y registros', icon: FileText },
                { title: 'Registros de temperatura', desc: 'Hojas diarias de control de camaras y congeladores, minimo 2 tomas/dia', icon: Thermometer },
                { title: 'Certificados de formacion', desc: 'De todos los manipuladores de alimentos. Actualizados y con nombre del curso', icon: BadgeCheck },
                { title: 'Fichas de alergenos', desc: 'De todos los platos de la carta. Actualizadas cada vez que cambia una receta', icon: Shield },
                { title: 'Contrato DDD', desc: 'Control de plagas vigente con empresa autorizada. Incluye certificados de tratamientos', icon: Bug },
                { title: 'Plan de limpieza', desc: 'Con frecuencias, productos, responsables y registros de ejecucion firmados', icon: Droplets },
                { title: 'Albaranes y facturas', desc: 'De todos los proveedores para trazabilidad. Minimo 2 anos de archivo', icon: FileCheck },
                { title: 'Analisis de agua', desc: 'Solo si usas pozo o deposito propio. Analisis fisicoquimico y microbiologico vigente', icon: Droplets },
              ].map(({ title, desc, icon: Icon }) => (
                <div key={title} className="flex gap-3 p-4 bg-slate-50 rounded-xl border border-slate-100">
                  <Icon className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-slate-800 text-sm">{title}</p>
                    <p className="text-slate-500 text-xs">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* ====== SECTION 9: RUTINAS ====== */}
            <h2 id="rutinas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Como mantener tu restaurante siempre listo
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La clave para superar cualquier inspeccion no es prepararse cuando te avisan — porque no te avisan. La unica estrategia que funciona es <strong>incorporar el control sanitario en la rutina diaria</strong> del restaurante. Cuando el cumplimiento es parte de la cultura del equipo, las inspecciones dejan de ser un motivo de estres y se convierten en una simple verificacion de lo que ya haces todos los dias.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Estas son las rutinas minimas recomendadas, organizadas por frecuencia. Asigna un responsable para cada tarea y establece un sistema de verificacion (firma, checklist digital o app):
            </p>
            <div className="space-y-6 mb-10">
              <div className="p-5 bg-green-50 border border-green-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-green-800">Rutinas diarias</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Registrar temperaturas de camaras y congeladores (apertura y cierre)</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Verificar etiquetado de productos abiertos (fecha apertura, caducidad secundaria)</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Limpieza de superficies de trabajo, suelos y equipos tras cada servicio</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Vaciado de contenedores de basura y limpieza de la zona de residuos</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />Verificar que todo el personal lleva uniforme limpio y cumple normas de higiene</li>
                </ul>
              </div>
              <div className="p-5 bg-blue-50 border border-blue-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-blue-600" />
                  <h3 className="font-bold text-blue-800">Rutinas semanales</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />Limpieza profunda de campanas extractoras y filtros</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />Revision de stock: retirar productos caducados o proximos a caducar</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />Verificar funcionamiento de termometros y calibrar si es necesario</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />Revisar que los productos quimicos estan correctamente almacenados y etiquetados</li>
                </ul>
              </div>
              <div className="p-5 bg-violet-50 border border-violet-200 rounded-xl">
                <div className="flex items-center gap-2 mb-3">
                  <Calendar className="w-5 h-5 text-violet-600" />
                  <h3 className="font-bold text-violet-800">Rutinas mensuales</h3>
                </div>
                <ul className="space-y-2 text-sm text-slate-700">
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />Autoinspeccion completa siguiendo el checklist de 15 puntos</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />Limpieza de detras y debajo de todos los equipos de cocina</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />Revision de juntas de puertas de camaras frigorificas</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />Actualizar fichas de alergenos si ha habido cambios en la carta</li>
                  <li className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-violet-500 flex-shrink-0 mt-0.5" />Verificar vigencia del contrato DDD y revisar trampas/cebos</li>
                </ul>
              </div>
            </div>

            {/* ====== SECTION 10: HERRAMIENTAS DIGITALES ====== */}
            <h2 id="herramientas" className="text-3xl font-bold text-slate-900 mb-6 scroll-mt-20">
              Herramientas digitales para el control sanitario
            </h2>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              La digitalizacion del control sanitario reduce errores, ahorra tiempo y genera registros automaticos que puedes mostrar al inspector al instante. Un restaurante que usa herramientas digitales para sus registros de autocontrol suele dedicar un <strong>70% menos de tiempo</strong> a la gestion documental y comete menos errores que uno que trabaja con papel.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Ademas, los registros digitales son mas dificiles de falsificar (tienen timestamps automaticos) y mas faciles de consultar durante una inspeccion — en lugar de buscar entre carpetas de papel, puedes mostrar al inspector los datos en una tablet en segundos. Estas son las herramientas clave:
            </p>
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              {[
                {
                  title: 'Software de gestion de costes con alergenos',
                  desc: 'PLEKO asigna alergenos a cada ingrediente y los hereda a las recetas automaticamente. La carta digital muestra los alergenos sin trabajo manual — cumplimiento garantizado del Reglamento 1169/2011.',
                  highlighted: true,
                },
                {
                  title: 'App de registros APPCC',
                  desc: 'Aplicaciones que digitalizan los registros de temperatura, limpieza y recepcion de mercancias. Eliminan las hojas de papel y generan historicos consultables al instante.',
                  highlighted: false,
                },
                {
                  title: 'Termometros digitales con registro automatico',
                  desc: 'Sondas conectadas por wifi que registran la temperatura de camaras cada 15 minutos y envian alertas si se sale del rango. Precio: 50-150 euros por sonda.',
                  highlighted: false,
                },
                {
                  title: 'OCR de facturas para trazabilidad',
                  desc: 'PLEKO escanea facturas de proveedores con IA y extrae automaticamente los productos, precios y lotes. Trazabilidad completa sin introducir datos a mano.',
                  highlighted: true,
                },
              ].map(({ title, desc, highlighted }) => (
                <div key={title} className={`p-5 rounded-xl border ${highlighted ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
                  <h3 className={`font-semibold mb-2 text-sm ${highlighted ? 'text-red-800' : 'text-slate-800'}`}>{title}</h3>
                  <p className="text-slate-600 text-sm">{desc}</p>
                </div>
              ))}
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-5 mb-6">
              <div className="flex items-start gap-3">
                <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800">Consejo clave</p>
                  <p className="text-amber-700 text-sm mt-1">
                    La mejor estrategia ante una inspeccion no es prepararse cuando llega el inspector, sino <strong>integrar el cumplimiento sanitario en la operativa diaria</strong>. Un restaurante que trabaja limpio todos los dias no necesita "prepararse" para una inspeccion — ya esta preparado. Las herramientas digitales te ayudan a mantener esta consistencia sin depender de la memoria o la buena voluntad del equipo.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Recuerda que el objetivo de una inspeccion sanitaria no es perjudicar al restaurante, sino <strong>proteger la salud de tus clientes</strong>. Un restaurante que cumple con la normativa no solo evita multas — tambien protege su reputacion, genera confianza en el cliente y trabaja en un entorno mas seguro y profesional para todo el equipo.
            </p>
            <p className="text-slate-700 text-lg leading-relaxed mb-6">
              Si necesitas ayuda para gestionar los <Link to="/alergenos-restaurante" className="text-red-600 hover:underline">alergenos de tus recetas</Link>, digitalizar la <Link to="/trazabilidad-alimentaria" className="text-red-600 hover:underline">trazabilidad de tus proveedores</Link> o implementar un sistema de <Link to="/appcc-restaurante" className="text-red-600 hover:underline">autocontrol APPCC</Link>, PLEKO puede ayudarte a automatizar buena parte de estos procesos.
            </p>
          </div>

          {/* ====== FAQ ====== */}
          <div id="faqs" className="mt-16 scroll-mt-20">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">
              Preguntas frecuentes sobre inspecciones de sanidad
            </h2>
            <div className="space-y-4">
              {faqs.map(({ q, a }) => (
                <details key={q} className="border border-slate-200 rounded-xl overflow-hidden">
                  <summary className="p-5 font-semibold text-slate-800 cursor-pointer hover:bg-slate-50 flex justify-between items-center list-none">
                    {q}
                    <span className="text-slate-400 text-xl ml-4 flex-shrink-0">+</span>
                  </summary>
                  <div className="px-5 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">{a}</div>
                </details>
              ))}
            </div>
          </div>

          {/* ====== KEY LEGISLATION ====== */}
          <div className="mt-12 p-6 bg-red-50 rounded-xl border border-red-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <FileText className="w-5 h-5 text-red-600" /> Legislacion de referencia
            </h3>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2">
                <span className="text-red-400 font-bold flex-shrink-0">1.</span>
                <span><strong>Reglamento (CE) 852/2004</strong> — Higiene de los productos alimenticios. Obliga a implementar un sistema APPCC.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-red-400 font-bold flex-shrink-0">2.</span>
                <span><strong>Reglamento (CE) 882/2004</strong> — Controles oficiales. Define como, cuando y quien realiza las inspecciones.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-red-400 font-bold flex-shrink-0">3.</span>
                <span><strong>Ley 17/2011</strong> — Seguridad alimentaria y nutricion. Establece el regimen sancionador (infracciones y multas).</span>
              </li>
              <li className="flex gap-2">
                <span className="text-red-400 font-bold flex-shrink-0">4.</span>
                <span><strong>Reglamento (UE) 1169/2011</strong> — Informacion alimentaria al consumidor. Obliga a declarar los 14 alergenos.</span>
              </li>
              <li className="flex gap-2">
                <span className="text-red-400 font-bold flex-shrink-0">5.</span>
                <span><strong>Real Decreto 109/2010</strong> — Formacion de manipuladores de alimentos. Responsabilidad del empresario.</span>
              </li>
            </ul>
          </div>

          {/* ====== INTERNAL LINKS ====== */}
          <div className="mt-12 p-6 bg-slate-50 rounded-xl border border-slate-200">
            <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
              <BookOpen className="w-5 h-5 text-red-600" /> Guias relacionadas
            </h3>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                { to: '/appcc-restaurante', label: 'Plan APPCC para restaurantes: guia completa' },
                { to: '/alergenos-restaurante', label: 'Alergenos en restaurantes: normativa y como cumplirla' },
                { to: '/trazabilidad-alimentaria', label: 'Trazabilidad alimentaria en hosteleria' },
                { to: '/desperdicio-alimentario-restaurante', label: 'Desperdicio alimentario: normativa y reduccion' },
                { to: '/control-horario-hosteleria', label: 'Control horario en hosteleria (RD-ley 8/2019)' },
                { to: '/escandallo', label: 'Que es un escandallo: guia completa' },
                { to: '/software-gestion-restaurante', label: 'Software de gestion para restaurantes' },
              ].map(({ to, label }) => (
                <Link key={to} to={to} className="flex items-center gap-2 text-sm text-red-600 hover:text-red-800 hover:underline">
                  <ArrowRight className="w-3 h-3 flex-shrink-0" /> {label}
                </Link>
              ))}
            </div>
          </div>

          {/* ====== FINAL CTA ====== */}
          <div className="mt-16 bg-gradient-to-r from-red-600 to-red-700 rounded-2xl p-8 text-center text-white">
            <Shield className="w-12 h-12 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-3">
              Manten tu restaurante siempre listo para la inspeccion
            </h2>
            <p className="text-red-100 mb-6 max-w-xl mx-auto">
              PLEKO gestiona alergenos automaticamente, digitaliza la trazabilidad con OCR de facturas y te ayuda a mantener el control de costes y registros al dia. 14 dias gratis, sin tarjeta.
            </p>
            <Link
              to="/register"
              className="inline-flex items-center gap-2 px-8 py-3 bg-white text-red-700 font-bold rounded-xl hover:bg-red-50 transition-colors"
            >
              Empezar gratis
              <ArrowRight className="w-4 h-4" />
            </Link>
            <p className="mt-3 text-red-200 text-sm">Sin tarjeta de credito. Configurado en 2 minutos.</p>
          </div>
        </section>
      </div>
    </>
  );
}
